/**
 * Contacts list page.
 *
 * Real table layout with named columns (Name / Phone / Email / Account /
 * Assigned to / Created), debounced search across name+phone+email,
 * assignee filter (manager+) and a "no account" toggle. Server-driven
 * pagination caps the page at 25 rows.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Plus, Search, Trash2, Users, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { type Column, DataTable } from "../components/DataTable";
import { ImportDialog } from "../components/ImportDialog";
import { SearchableUserPicker } from "../components/SearchableUserPicker";
import { TagFilterChips } from "../components/tags/TagFilterChips";
import { type Contact, accountsApi, contactsApi, usersApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/contacts/")({
  component: ContactsListPage,
});

const PAGE_SIZE = 25;

function ContactsListPage() {
  const user = useAuthStore((s) => s.user);
  const isManagerPlus = user?.role === "admin" || user?.role === "manager";
  const queryClient = useQueryClient();

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [assignedTo, setAssignedTo] = useState<string>("");
  const [unassignedAccount, setUnassignedAccount] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const filters = {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    q: debouncedQ.trim() || undefined,
    assigned_to_id: assignedTo || undefined,
    unassigned_account: unassignedAccount || undefined,
    tag_ids: tagIds.length > 0 ? tagIds : undefined,
  };

  const listQuery = useQuery({
    queryKey: ["contacts", filters],
    queryFn: () => contactsApi.list(filters),
  });

  const usersQuery = useQuery({
    queryKey: ["users", "active"],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isManagerPlus,
  });
  const usersById = useMemo(() => {
    const m = new Map<string, { id: string; email: string; name: string | null }>();
    for (const u of usersQuery.data?.items ?? []) m.set(u.id, u);
    return m;
  }, [usersQuery.data]);

  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200 }],
    queryFn: () => accountsApi.list({ limit: 200 }),
  });
  const accountsById = useMemo(() => {
    const m = new Map<string, { id: string; name: string }>();
    for (const a of accountsQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [accountsQ.data]);

  const data = listQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasFilter = Boolean(debouncedQ || assignedTo || unassignedAccount || tagIds.length > 0);

  const columns: Column<Contact>[] = [
    {
      header: "Name",
      width: "min-w-[12rem]",
      cell: (c) => <span>{c.name}</span>,
    },
    {
      header: "Phone",
      width: "w-40",
      cell: (c) => <span className="font-mono text-xs">{c.phone}</span>,
    },
    {
      header: "Email",
      width: "min-w-[12rem]",
      hideOnSmall: true,
      cell: (c) => <span className="truncate text-xs opacity-80">{c.email ?? "—"}</span>,
    },
    {
      header: "Account",
      width: "w-40",
      hideOnSmall: true,
      cell: (c) => {
        if (!c.account_id) return <span className="text-xs opacity-50">—</span>;
        const a = accountsById.get(c.account_id);
        const label = a?.name ?? `${c.account_id.slice(0, 8)}…`;
        return (
          // stopPropagation so clicking the link only follows it; the
          // row's own onClick (open the contact) doesn't also fire.
          <Link
            to="/accounts/$accountId"
            params={{ accountId: c.account_id }}
            onClick={(e) => e.stopPropagation()}
            className="truncate text-xs underline-offset-2 hover:underline"
            title={label}
          >
            {label}
          </Link>
        );
      },
    },
    {
      header: "Assigned to",
      width: "w-36",
      hideOnSmall: true,
      cell: (c) => {
        if (!c.assigned_to_id) return <span className="text-xs opacity-50">Unassigned</span>;
        const u = usersById.get(c.assigned_to_id);
        const label = u ? u.name || u.email.split("@")[0] : `${c.assigned_to_id.slice(0, 8)}…`;
        // /team/$userId is manager+ only; for sales we render plain
        // text (sales also doesn't fetch usersById, so they won't see
        // resolved names — we keep the truncated id fallback for
        // continuity rather than hiding the column entirely).
        if (!isManagerPlus) {
          return <span className="text-xs">{label}</span>;
        }
        return (
          <Link
            to="/team/$userId"
            params={{ userId: c.assigned_to_id }}
            onClick={(e) => e.stopPropagation()}
            className="truncate text-xs underline-offset-2 hover:underline"
            title={label}
          >
            {label}
          </Link>
        );
      },
    },
    {
      header: "Created",
      width: "w-28",
      align: "right",
      hideOnSmall: true,
      cell: (c) => (
        <span className="text-xs opacity-60">{new Date(c.created_at).toLocaleDateString()}</span>
      ),
    },
  ];

  return (
    <div className="page-shell space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
          <p className="text-sm opacity-60">
            People you can reach. Phone is the primary key — duplicates are blocked.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Link
              to="/contacts/trash"
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Trash"
            >
              <Trash2 className="size-4" aria-hidden />
              <span className="hidden sm:inline">Trash</span>
            </Link>
          )}
          {isManagerPlus && (
            <ImportDialog
              entityLabel="Contacts"
              importPath="/api/contacts/import"
              templatePath="/api/contacts/import/template"
              columns={["name", "phone", "email", "address", "account"]}
              onImported={() => queryClient.invalidateQueries({ queryKey: ["contacts"] })}
            />
          )}
          <Link
            to="/contacts/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">New contact</span>
            <span className="sm:hidden">New</span>
          </Link>
        </div>
      </header>

      <section className="space-y-2 rounded-xl border border-[var(--border)] p-3">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 opacity-50"
            aria-hidden
          />
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Search name, phone, or email…"
            className="w-full rounded-md border border-[var(--border)] bg-transparent py-2 pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setDebouncedQ("");
                setPage(0);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 opacity-50 hover:opacity-100"
              aria-label="Clear search"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {isManagerPlus && (
            <SearchableUserPicker
              value={assignedTo}
              onChange={(v) => {
                setAssignedTo(v);
                setPage(0);
              }}
              users={usersQuery.data?.items ?? []}
              loading={usersQuery.isLoading}
              emptyLabel="All assignees"
              ariaLabel="Filter by assignee"
            />
          )}
          <label className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1">
            <input
              type="checkbox"
              checked={unassignedAccount}
              onChange={(e) => {
                setUnassignedAccount(e.target.checked);
                setPage(0);
              }}
            />
            <span>No account</span>
          </label>
          {hasFilter && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setDebouncedQ("");
                setAssignedTo("");
                setUnassignedAccount(false);
                setTagIds([]);
                setPage(0);
              }}
              className="rounded-md px-2 py-1 text-xs underline opacity-70 hover:opacity-100 sm:ml-auto"
            >
              Clear filters
            </button>
          )}
        </div>
        <TagFilterChips
          value={tagIds}
          onChange={(next) => {
            setTagIds(next);
            setPage(0);
          }}
        />
      </section>

      {listQuery.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : listQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load contacts: {(listQuery.error as Error).message}
        </div>
      ) : (
        <>
          <div className="text-xs opacity-60">
            {data?.total ?? 0} {data?.total === 1 ? "contact" : "contacts"}
            {hasFilter ? " match your filters" : ""}
          </div>
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(c) => c.id}
            rowHref={(c) => `/contacts/${c.id}`}
            empty={<EmptyState hasFilter={hasFilter} />}
          />
          <Pager
            page={page}
            totalPages={totalPages}
            total={data?.total ?? 0}
            shown={data?.items.length ?? 0}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          />
        </>
      )}
    </div>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  if (hasFilter) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--border)] p-8 text-center">
        <Search className="mx-auto mb-3 size-7 opacity-40" aria-hidden />
        <p className="text-sm font-medium">No matches</p>
        <p className="mt-1 text-sm opacity-60">Try a different search or clear the filters.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Users className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">No contacts yet</p>
      <p className="mt-1 text-sm opacity-60">
        Create your first contact to start tracking conversations.
      </p>
      <Link
        to="/contacts/new"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
      >
        <Plus className="size-4" aria-hidden />
        Create your first contact
      </Link>
    </div>
  );
}

function Pager({
  page,
  totalPages,
  total,
  shown,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  total: number;
  shown: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between text-xs opacity-70">
      <span>
        Page {page + 1} of {totalPages} · {shown} shown
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={page === 0}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages - 1}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
